import axiosInstance from '@/utils/axios';
import { hospitalHeaders } from '@/utils';

/** Floor events. Property shapes match the backend analytics contract. */
export const AnalyticsEvent = {
    LoginSucceeded: 'Login Succeeded',
    CensusPrinted: 'Census Printed',
    NoteAdded: 'Note Added'
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

export type AnalyticsPropertyValue = string | number | string[] | null | undefined;

export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

export type TrackAnalyticsEventParams = {
    event: AnalyticsEventName;
    properties?: AnalyticsProperties;
    hospitalId?: string | null;
};

export type AnalyticsEventResponse = {
    accepted: boolean;
    role?: string;
    hospital_id?: string;
    forwarded?: boolean;
};

export type AnalyticsActor = {
    id?: string | number | null;
    role?: { key?: string | null; value?: string | null } | null;
    currentHospitalId?: string | null;
    hospital?: { id?: string | null } | null;
    hospitals?: Array<{ id: string; isDefault?: boolean }> | null;
};

type AnalyticsSliceState = {
    auth?: { user?: AnalyticsActor | null };
    hospital?: { currentHospitalId?: string | null };
};

const compactProperties = (properties: AnalyticsProperties | undefined): Record<string, string | string[]> | undefined => {
    if (!properties) {
        return undefined;
    }

    const result: Record<string, string | string[]> = {};
    Object.entries(properties).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            const items = [...new Set(value.map((item) => item.trim()).filter(Boolean))];
            if (items.length > 0) {
                result[key] = items;
            }
            return;
        }
        if (value == null) {
            return;
        }
        const text = String(value).trim();
        if (text) {
            result[key] = text;
        }
    });

    return Object.keys(result).length > 0 ? result : undefined;
};

export const uniqueUnitIds = (unitIds: Array<string | null | undefined> | undefined): string[] => [
    ...new Set((unitIds ?? []).map((unitId) => unitId?.trim() ?? '').filter(Boolean))
];

export const resolveAnalyticsHospitalId = (actor?: AnalyticsActor | null, explicit?: string | null): string | undefined => {
    const preferred = explicit?.trim();
    if (preferred) {
        return preferred;
    }
    if (!actor) {
        return undefined;
    }

    const current = actor.currentHospitalId?.trim();
    if (current) {
        return current;
    }
    const hospital = actor.hospital?.id?.trim();
    if (hospital) {
        return hospital;
    }
    const defaultHospital = actor.hospitals?.find((item) => item.isDefault)?.id?.trim();
    if (defaultHospital) {
        return defaultHospital;
    }
    return actor.hospitals?.[0]?.id?.trim() || undefined;
};

export const resolveAnalyticsRole = (actor?: AnalyticsActor | null): string | undefined => {
    const role = actor?.role?.key?.trim() || actor?.role?.value?.trim();
    return role || undefined;
};

/** `Census Printed`. `unit_ids` is omitted when no unit UUID is known. */
export const trackCensusPrinted = ({ hospitalId, unitIds }: { hospitalId?: string | null; unitIds?: Array<string | null | undefined> }) => {
    const ids = uniqueUnitIds(unitIds);
    return trackAnalyticsEvent({
        event: AnalyticsEvent.CensusPrinted,
        hospitalId,
        properties: ids.length > 0 ? { unit_ids: ids } : undefined
    });
};

export const readAnalyticsContext = (getState: () => unknown) => {
    const state = getState() as AnalyticsSliceState;
    const user = state.auth?.user ?? null;
    return {
        userId: user?.id == null || String(user.id).trim() === '' ? undefined : String(user.id),
        role: resolveAnalyticsRole(user),
        hospitalId: resolveAnalyticsHospitalId(user, state.hospital?.currentHospitalId)
    };
};

/**
 * `POST /analytics/events` — header `X-Hospital-Id` when known.
 * The backend forwards the event to MixPanel. A failure here must not toast or block the user action.
 */
export const trackAnalyticsEvent = async ({ event, properties, hospitalId }: TrackAnalyticsEventParams): Promise<void> => {
    const resolvedHospitalId = hospitalId?.trim() || undefined;
    try {
        const bodyProperties = compactProperties(properties);
        await axiosInstance.post<AnalyticsEventResponse>(
            'analytics/events',
            bodyProperties ? { event, properties: bodyProperties } : { event },
            {
                ...hospitalHeaders(resolvedHospitalId),
                skipErrorReporting: true
            }
        );
    } catch {
        // Analytics is best-effort.
    }
};
